/** Parsed Client Portal request notes (TripBooking serialization). */

export interface ClientPassengerGroup {
  name: string;
  count: number;
  lead?: string;
  phone?: string;
}

export interface ParsedClientRequestNotes {
  /** Free-text special requirements (excluding vehicle pref + groups block). */
  specialNotes: string | null;
  vehiclePreference: string | null;
  passengerGroups: ClientPassengerGroup[];
  /** Raw remainder when JSON parse fails or structure unknown. */
  rawNotes: string | null;
}

const VEHICLE_PREF_RE = /^Vehicle preference:\s*(.+)$/im;
const GROUPS_RE = /Passenger groups:\s*(\[[\s\S]*\])\s*$/m;

function normalizeGroup(raw: unknown): ClientPassengerGroup | null {
  if (!raw || typeof raw !== 'object') return null;
  const g = raw as Record<string, unknown>;
  const name = typeof g.name === 'string' ? g.name : 'Group';
  const count = typeof g.count === 'number' ? g.count : Number(g.count);
  if (!Number.isFinite(count) || count < 0) return null;
  return {
    name,
    count,
    lead: typeof g.lead === 'string' ? g.lead : undefined,
    phone: typeof g.phone === 'string' ? g.phone : undefined,
  };
}

/**
 * Parse Client New Request metadata.
 * Format (legacy, all on jobs.notes): special notes + optional "Vehicle preference: …" + "Passenger groups: […]".
 * Format (current): special notes on jobs.notes; vehicle/groups on job_legs.notes — join before parse.
 */
export function parseClientRequestNotes(notes?: string | null): ParsedClientRequestNotes {
  if (!notes?.trim()) {
    return {
      specialNotes: null,
      vehiclePreference: null,
      passengerGroups: [],
      rawNotes: null,
    };
  }

  let rest = notes.trim();
  let vehiclePreference: string | null = null;
  let passengerGroups: ClientPassengerGroup[] = [];

  const prefMatch = rest.match(VEHICLE_PREF_RE);
  if (prefMatch) {
    vehiclePreference = prefMatch[1].trim();
    rest = rest.replace(VEHICLE_PREF_RE, '').trim();
  }

  const groupsMatch = rest.match(GROUPS_RE);
  if (groupsMatch) {
    try {
      const parsed = JSON.parse(groupsMatch[1]) as unknown;
      if (Array.isArray(parsed)) {
        passengerGroups = parsed
          .map(normalizeGroup)
          .filter((g): g is ClientPassengerGroup => g != null);
      }
    } catch {
      // keep raw; leave groups empty
    }
    rest = rest.replace(GROUPS_RE, '').trim();
  }

  const specialNotes = rest.length > 0 ? rest : null;

  return {
    specialNotes,
    vehiclePreference,
    passengerGroups,
    rawNotes:
      passengerGroups.length === 0 && /Passenger groups:/i.test(notes)
        ? notes
        : null,
  };
}

/** Re-serialize leg notes for storage (user text + optional vehicle/groups meta). */
export function composeClientRequestLegNotes(params: {
  specialNotes?: string | null;
  vehiclePreference?: string | null;
  passengerGroups?: ClientPassengerGroup[];
}): string | null {
  const parts: string[] = [];
  const special = (params.specialNotes ?? '').trim();
  if (special) parts.push(special);

  const pref = (params.vehiclePreference ?? '').trim();
  if (pref) parts.push(`Vehicle preference: ${pref}`);

  const groups = (params.passengerGroups ?? [])
    .map((g) => normalizeGroup(g))
    .filter((g): g is ClientPassengerGroup => g != null);
  if (groups.length > 0) {
    parts.push(
      `Passenger groups: ${JSON.stringify(
        groups.map((g) => ({
          name: g.name,
          count: g.count,
          ...(g.lead ? { lead: g.lead } : {}),
          ...(g.phone ? { phone: g.phone } : {}),
        })),
      )}`,
    );
  }

  return parts.length > 0 ? parts.join('\n') : null;
}
